import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, User, Users, Sparkles, Camera, Upload,
  Shield, MapPin, Check, X, Loader2
} from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { useAuth } from '@context/AuthContext';
import { usePublicConfig } from '@context/PublicConfigContext';
import api from '@utils/api';
import { resolveProfileDisplaySrc } from '@utils/profileImageSrc';
import { readPendingJoinIntent } from '@utils/pendingJoinIntent';
import toast from 'react-hot-toast';

const INTEREST_OPTIONS = [
  'Badminton', 'Running', 'Yoga', 'Gym', 'Swimming', 'Cycling', 'Tennis',
  'Basketball', 'Football', 'Cricket', 'Hiking', 'Cooking', 'Movies',
  'Data Science', 'Photography', 'Music', 'Gaming', 'Reading', 'Dancing',
];

const BIO_MAX_LEN = 300;

const TOTAL_STEPS = 6;
/** Steps where "Skip for now" is hidden (identity: name/age/gender; bio + interests). */
const STEPS_SKIP_DISALLOWED = new Set([1, 2]);
/** Profile upload, selfie, and face check — skip any of these → go straight to location (step 6). */
const VERIFICATION_STEPS = new Set([3, 4, 5]);
const LOCATION_STEP = 6;

function revokeObjectUrlMaybe(url) {
  if (url && String(url).startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }
}

function resolvedGenderMatchFromVerifyResponse(data) {
  if (typeof data.genderMatch === 'boolean') return data.genderMatch;
  const g = data.selectedGender;
  if (g !== 'male' && g !== 'female') return true;
  const d = (data.detectedGender || '').toLowerCase();
  return d === g;
}

/** Human-readable failure when `/aws/verify-face` returns verified: false. */
function faceVerificationFailureMessage(data) {
  if (!data || data.verified) return '';
  const sim = Number(data.similarity) || 0;
  const faceOk = typeof data.faceMatch === 'boolean' ? data.faceMatch : sim >= 90;
  const g = data.selectedGender;
  const genderRelevant = g === 'male' || g === 'female';
  const genderOk = typeof data.genderMatch === 'boolean'
    ? data.genderMatch
    : resolvedGenderMatchFromVerifyResponse(data);
  const ageOk = data.ageMatch !== false;

  if (!faceOk) {
    return sim > 0
      ? `Similarity: ${data.similarity}% (needs 90%+). Try clearer, well-lit photos.`
      : 'Photos could not be matched closely enough. Try clearer, well-lit photos.';
  }
  if (!genderOk && genderRelevant) {
    return "Your profile gender doesn't match what we detected in your selfie. Update gender on your profile step if it's incorrect, or retake the selfie.";
  }
  if (!ageOk && data.failureStep === 'age') {
    const r = data.detectedAgeRange;
    const stated = data.statedAge;
    if (r && stated != null) {
      return `Your profile age (${stated}) is outside the range we estimated from your selfie (${r.low}–${r.high} years). Tap below to review your name, age, and gender, then try verification again.`;
    }
    return 'Your profile age does not match what we estimated from your selfie. Review your details below and try again.';
  }
  return 'Photos could not be verified. Try clearer photos.';
}

/** Title-case each whitespace-separated word (e.g. "abc xyz" → "Abc Xyz"). */
function formatFullNameInput(value) {
  return String(value ?? '')
    .split(/(\s+)/)
    .map((segment) => {
      if (/^\s+$/.test(segment)) return segment;
      if (!segment) return '';
      return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
    })
    .join('');
}

const OnboardingPage = () => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [bio, setBio] = useState('');
  const [interests, setInterests] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState('');
  const [selfiePreview, setSelfiePreview] = useState('');
  const [selfieBlob, setSelfieBlob] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  /** After skipping verification, Back from location should return to bio/interests, not an empty verify step. */
  const verificationSkippedRef = useRef(false);
  /** After age mismatch, saving identity jumps back to verification (step 5) instead of bio/interests. */
  const pendingReturnToVerificationRef = useRef(false);
  const [returningFromAgeVerification, setReturningFromAgeVerification] = useState(false);

  const { user, updateUser } = useAuth();
  const { siteOrigin } = usePublicConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const fromLocation = location.state?.from;
  const fromPath = fromLocation?.pathname || '/feed';
  const from = `${fromPath}${fromLocation?.search || ''}${fromLocation?.hash || ''}`;

  const resolvePostOnboardingPath = () => {
    const pendingJoin = readPendingJoinIntent();
    if (pendingJoin?.path) return pendingJoin.path;
    return from;
  };

  useEffect(() => {
    if (user) {
      if (user.name && user.name !== 'User') setName(formatFullNameInput(user.name));
      if (user.age) setAge(String(user.age));
      if (user.gender && user.gender !== 'prefer_not_to_say') setGender(user.gender);
      if (user.interests?.length) setInterests(user.interests);
      if (user.bio != null && user.bio !== '') setBio(user.bio);
    }
  }, [user]);

  /** Returning to step 5 after location: restore verified UI from server if local state was cleared. */
  useLayoutEffect(() => {
    if (step !== 5 || !user?.faceVerified) return;
    setVerificationStatus((prev) => {
      if (prev === 'failed' || prev === 'verifying') return prev;
      if (prev === 'verified') return prev;
      return 'verified';
    });
  }, [step, user?.faceVerified]);

  /** Already verified users skip upload + selfie (steps 3–4). */
  useEffect(() => {
    if (!user?.faceVerified) return;
    if (step === 3 || step === 4) setStep(5);
  }, [user?.faceVerified, step]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  /** Stop camera when leaving the selfie step (back, skip, or next). */
  useEffect(() => {
    if (step === 4) return;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, [step]);

  /**
   * <video> only mounts after cameraActive is true, but startCamera used to set srcObject
   * before that — videoRef was always null. Attach the stream after mount.
   */
  useEffect(() => {
    if (step !== 4 || !cameraActive || !streamRef.current) return undefined;

    let cancelled = false;
    let rafId = 0;

    const attach = () => {
      if (cancelled) return;
      const video = videoRef.current;
      const stream = streamRef.current;
      if (!video || !stream) {
        rafId = requestAnimationFrame(attach);
        return;
      }
      video.srcObject = stream;
      video.muted = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      const p = video.play();
      if (p !== undefined) {
        p.catch(() => {
          toast.error('Could not start camera preview. Try again or use another browser.');
        });
      }
    };
    attach();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      const v = videoRef.current;
      if (v) v.srcObject = null;
    };
  }, [step, cameraActive]);

  /** Face already verified: 1–2 → 5 (summary) → 6 (location), no upload/selfie steps. */
  const FACE_VERIFIED_FLOW = !!user?.faceVerified;
  const progressPercent = FACE_VERIFIED_FLOW
    ? step <= 2
      ? (step / 5) * 100
      : step === 5
        ? (4 / 5) * 100
        : step === 6
          ? (5 / 5) * 100
          : (4 / 5) * 100
    : (step / TOTAL_STEPS) * 100;

  const nextStep = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  const prevStep = () =>
    setStep((s) => {
      if (s === LOCATION_STEP && verificationSkippedRef.current && !user?.faceVerified) {
        verificationSkippedRef.current = false;
        return 2;
      }
      if (FACE_VERIFIED_FLOW) {
        if (s === LOCATION_STEP) return 5;
        if (s === 5) return 2;
      }
      return Math.max(s - 1, 1);
    });

  const handleIdentityStep = async () => {
    if (!name.trim()) return toast.error('Please enter your name');
    if (!age || age < 13 || age > 120) return toast.error('Please enter a valid age (13-120)');
    if (!gender) return toast.error('Please select a gender option');
    setLoading(true);
    try {
      const formattedName = formatFullNameInput(name).trim();
      const { data } = await api.post('/user/basic-info', {
        name: formattedName,
        age: Number(age),
      });
      updateUser(data.user);
      const { data: dataGender } = await api.post('/user/gender', { gender });
      updateUser(dataGender.user);
      if (pendingReturnToVerificationRef.current) {
        pendingReturnToVerificationRef.current = false;
        setReturningFromAgeVerification(false);
        setVerificationStatus(null);
        setVerificationData(null);
        setStep(5);
        toast.success('Details saved. Run verification again when you are ready.');
        return;
      }
      nextStep();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const handleBioAndInterests = async () => {
    if (interests.length === 0) return toast.error('Select at least one interest');
    setLoading(true);
    try {
      const formattedName = formatFullNameInput(name).trim();
      const { data: basicData } = await api.post('/user/basic-info', {
        name: formattedName,
        age: Number(age),
        bio: bio.trim(),
      });
      updateUser(basicData.user);
      const { data } = await api.post('/user/interests', { interests });
      updateUser(data.user);
      if (data.user?.faceVerified) {
        setStep(5);
      } else {
        nextStep();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  /** Selfie only — profile uses POST /api/upload-profile */
  const uploadSelfieToApi = async (file) => {
    const formData = new FormData();
    formData.append('type', 'selfie');
    formData.append('file', file);
    const { data } = await api.post('/aws/upload', formData);
    return data.fileUrl;
  };

  const uploadErrorMessage = (err) =>
    err.response?.data?.message
    || (err.code === 'ERR_NETWORK' ? 'Network error — check your connection and API URL.' : '')
    || err.message
    || 'Upload failed. Please try again.';

  const handleProfileUpload = async () => {
    if (!profileImage) return toast.error('Please select a profile photo');
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('profileImage', profileImage);
      const { data } = await api.post('/upload-profile', formData);
      const bust = Date.now();
      updateUser({
        ...(user || {}),
        profileImage: data.imageUrl,
        profileImageUrl: '',
      });
      setProfileImage(null);
      setProfilePreview(
        resolveProfileDisplaySrc({ profileImage: data.imageUrl }, siteOrigin, bust),
      );
      toast.success('Profile photo uploaded!');
      nextStep();
    } catch (err) {
      toast.error(uploadErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Camera is not available in this browser or context (use HTTPS or localhost).');
      return;
    }
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 } },
        });
      }
      streamRef.current = stream;
      setCameraActive(true);
    } catch (err) {
      const msg = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError'
        ? 'Camera access denied. Allow camera for this site in your browser settings.'
        : 'Could not open camera. Check permissions and try again.';
      toast.error(msg);
    }
  };

  const takeSelfie = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    if (!video.videoWidth || !video.videoHeight) {
      toast.error('Wait until you see the live preview, then capture.');
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      setSelfieBlob(blob);
      setSelfiePreview((prev) => {
        revokeObjectUrlMaybe(prev);
        return URL.createObjectURL(blob);
      });
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      setCameraActive(false);
    }, 'image/jpeg', 0.9);
  };

  const handleSelfieUpload = async () => {
    if (!selfieBlob) return toast.error('Please take a selfie first');
    setLoading(true);
    try {
      const file = new File([selfieBlob], 'selfie.jpg', { type: 'image/jpeg' });
      await uploadSelfieToApi(file);
      toast.success('Selfie uploaded!');
      nextStep();
    } catch (err) {
      toast.error(uploadErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  /** One primary CTA: open camera → capture → upload (works well on MacBook with Chrome/Safari). */
  const handleSelfieStepPrimary = () => {
    if (selfieBlob) {
      handleSelfieUpload();
      return;
    }
    if (cameraActive) {
      takeSelfie();
      return;
    }
    startCamera();
  };

  const handleVerification = async () => {
    setLoading(true);
    setVerificationStatus('verifying');
    try {
      const { data } = await api.post('/aws/verify-face');
      updateUser(data.user);
      setVerificationData(data);
      setVerificationStatus(data.verified ? 'verified' : 'failed');
      if (data.verified) {
        verificationSkippedRef.current = false;
        toast.success('Face verified successfully!');
      } else {
        toast.error(faceVerificationFailureMessage(data) || 'Verification failed. Please try again.');
      }
    } catch (err) {
      setVerificationStatus('failed');
      toast.error(err.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLocation = async () => {
    setLoading(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
        });
      });
      const { longitude, latitude } = position.coords;
      const { data } = await api.post('/user/location', { longitude, latitude });
      updateUser(data.user);
      toast.success('Welcome to Yaarvi!');
      navigate(resolvePostOnboardingPath(), { replace: true });
    } catch (err) {
      if (err.code) {
        toast.error('Location permission denied. You can set it later.');
        try {
          const { data } = await api.post('/user/complete');
          updateUser(data.user);
        } catch {}
        navigate(resolvePostOnboardingPath(), { replace: true });
      } else {
        toast.error(err.response?.data?.message || 'Failed to save location');
      }
    } finally {
      setLoading(false);
    }
  };

  const skipVerificationToLocation = () => {
    const alreadyVerified = !!user?.faceVerified;
    if (!alreadyVerified) {
      verificationSkippedRef.current = true;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
    if (!alreadyVerified) {
      setSelfiePreview((prev) => {
        revokeObjectUrlMaybe(prev);
        return '';
      });
      setSelfieBlob(null);
      setVerificationStatus(null);
      setVerificationData(null);
      setProfileImage(null);
      setProfilePreview((prev) => {
        revokeObjectUrlMaybe(prev);
        return '';
      });
    }
    setStep(LOCATION_STEP);
  };

  const skipAndContinue = async () => {
    if (STEPS_SKIP_DISALLOWED.has(step)) {
      if (step === 1) {
        toast.error('Please enter your name, age, and gender to continue');
        return;
      }
      toast.error('Select at least one interest to continue');
      return;
    }
    if (VERIFICATION_STEPS.has(step)) {
      skipVerificationToLocation();
      return;
    }
    if (step === TOTAL_STEPS) {
      try {
        const { data } = await api.post('/user/complete');
        updateUser(data.user);
      } catch {}
      navigate(resolvePostOnboardingPath(), { replace: true });
    } else {
      nextStep();
    }
  };

  const toggleInterest = (interest) => {
    setInterests((prev) =>
      prev.includes(interest) ? prev.filter((i) => i !== interest) : [...prev, interest]
    );
  };

  const onFileSelect = async (e) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      input.value = '';
      return;
    }
    const extOk = /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?|avif|svg|ico)$/i.test(file.name || '');
    const typeOk =
      !file.type ||
      file.type.startsWith('image/') ||
      (file.type === 'application/octet-stream' && extOk);
    if (!typeOk) {
      toast.error('Please choose an image file');
      input.value = '';
      return;
    }

    let toUpload = file;
    if (file.size > 2 * 1024 * 1024) {
      try {
        toUpload = await imageCompression(file, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
        });
      } catch {
        toast.error('Could not compress image. Try a smaller file.');
        input.value = '';
        return;
      }
    }

    setProfileImage(toUpload);
    setProfilePreview((prev) => {
      revokeObjectUrlMaybe(prev);
      return URL.createObjectURL(toUpload);
    });
    input.value = '';
  };

  const goToDetailsAfterAgeMismatch = () => {
    pendingReturnToVerificationRef.current = true;
    setReturningFromAgeVerification(true);
    const u = verificationData?.user || user;
    if (u?.name) setName(formatFullNameInput(u.name));
    if (u?.age != null) setAge(String(u.age));
    if (u?.gender) setGender(u.gender);
    if (u?.bio != null) setBio(u.bio);
    setVerificationStatus(null);
    setVerificationData(null);
    setStep(1);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <StepCard
            badge="Step 1 of 2"
            title="Tell us about yourself"
            subtitle={
              returningFromAgeVerification
                ? 'Your name, age, and gender are filled from your profile. Adjust if needed, then continue — you will return to face verification.'
                : 'This helps us personalize your experience and keep Yaarvi authentic'
            }
          >
            <div className="rounded-xl border border-surface-700/90 bg-surface-800/35 p-4 sm:p-5 space-y-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-surface-500">
                Name, age & gender
              </p>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-surface-400">
                  Full Name
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-500">
                    <User size={18} />
                  </div>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(formatFullNameInput(e.target.value))}
                    className="w-full rounded-xl border border-surface-700 bg-surface-800/60 py-3.5 pl-11 pr-4 text-sm text-white placeholder-surface-500 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30"
                    placeholder="Your name"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-surface-400">
                  Age
                </label>
                <input
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="w-full rounded-xl border border-surface-700 bg-surface-800/60 py-3.5 px-4 text-sm text-white placeholder-surface-500 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30"
                  placeholder="Your age"
                  min={13}
                  max={120}
                  required
                />
              </div>
              <div className="pt-1">
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-surface-400">
                  Gender
                </label>
                <div className="space-y-3">
                {[
                  { value: 'male', label: 'Boy', icon: User },
                  { value: 'female', label: 'Girl', icon: Users },
                ].map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setGender(value)}
                    className={`w-full flex items-center gap-4 rounded-xl border p-4 transition-all ${
                      gender === value
                        ? 'border-brand-500 bg-brand-500/10 shadow-sm shadow-brand-500/20'
                        : 'border-surface-700 bg-surface-800/40 hover:border-surface-600'
                    }`}
                  >
                    <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                      gender === value ? 'bg-brand-500/20 text-brand-400' : 'bg-surface-700 text-surface-400'
                    }`}>
                      <Icon size={20} />
                    </div>
                    <span className={`text-sm font-semibold ${
                      gender === value ? 'text-white' : 'text-surface-300'
                    }`}>
                      {label}
                    </span>
                    <div className="ml-auto">
                      <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        gender === value
                          ? 'border-brand-500 bg-brand-500'
                          : 'border-surface-600'
                      }`}>
                        {gender === value && <Check size={12} className="text-white" />}
                      </div>
                    </div>
                  </button>
                ))}
                </div>
              </div>
            </div>
          </StepCard>
        );

      case 2:
        return (
          <StepCard
            badge="Step 2 of 2"
            title="Bio & interests"
            subtitle="Help others find you and match you with the right vibes"
          >
            <div className="space-y-6">
              <div className="rounded-xl border border-surface-700/90 bg-surface-800/35 p-4 sm:p-5">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-surface-500">
                    About you
                  </label>
                  <span className="text-[11px] font-medium text-surface-500">
                    {bio.length}/{BIO_MAX_LEN}
                  </span>
                </div>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value.slice(0, BIO_MAX_LEN))}
                  rows={4}
                  className="w-full resize-y rounded-xl border border-surface-700 bg-surface-800/60 py-3.5 px-4 text-sm text-white placeholder-surface-500 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30 min-h-[5.5rem]"
                  placeholder="A short intro — hobbies, what you like to play, or what you are looking for on Yaarvi (optional)"
                  maxLength={BIO_MAX_LEN}
                />
                <p className="mt-1.5 text-[11px] text-surface-500">
                  Shown on your profile so others can get to know you.
                </p>
              </div>
              <div className="rounded-xl border border-surface-700/90 bg-surface-800/35 p-4 sm:p-5">
                <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-surface-500">
                  Interests
                </p>
                <div className="flex flex-wrap gap-2 max-h-[240px] overflow-y-auto pr-1 custom-scrollbar">
                  {INTEREST_OPTIONS.map((interest) => (
                    <button
                      type="button"
                      key={interest}
                      onClick={() => toggleInterest(interest)}
                      className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                        interests.includes(interest)
                          ? 'bg-gradient-to-r from-brand-500 to-violet-500 text-white shadow-sm shadow-brand-500/20'
                          : 'bg-surface-800/60 text-surface-300 border border-surface-700 hover:border-surface-500'
                      }`}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
                {interests.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {interests.map((i) => (
                      <span key={i} className="inline-flex items-center gap-1 rounded-full bg-brand-500/10 px-3 py-1 text-xs font-semibold text-brand-400">
                        {i}
                        <button type="button" onClick={() => toggleInterest(i)} className="hover:text-brand-200 transition-colors">
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </StepCard>
        );

      case 3:
        return (
          <StepCard
            badge="Step 2 of 2 — Verification"
            title="Verify your identity"
            subtitle="Upload a clear profile photo to start the verification process."
            icon={Shield}
            subSteps={[
              { num: 1, label: 'Upload Photo', active: true },
              { num: 2, label: 'Take Selfie', active: false },
              { num: 3, label: 'Verification', active: false },
            ]}
          >
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-surface-600 rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-brand-500/50 transition-all group"
            >
              {profilePreview || user?.profileImage ? (
                <img
                  src={profilePreview || resolveProfileDisplaySrc(user, siteOrigin)}
                  alt="Profile"
                  className="h-32 w-32 rounded-2xl object-cover"
                />
              ) : (
                <>
                  <div className="h-14 w-14 rounded-2xl bg-surface-800 flex items-center justify-center mb-3 group-hover:bg-brand-500/10 transition-colors">
                    <Upload size={24} className="text-brand-400" />
                  </div>
                  <p className="text-sm font-semibold text-surface-300">Upload Profile Photo</p>
                  <p className="text-xs text-surface-500 mt-1">Common formats (incl. HEIC, WebP) up to 10MB</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onFileSelect}
              className="hidden"
            />
            <div className="mt-3 rounded-xl bg-surface-800/60 border border-surface-700 p-3 flex items-center gap-2">
              <span className="text-xs">🔒</span>
              <span className="text-xs text-surface-400">Your photos are encrypted and used only for verification</span>
            </div>
          </StepCard>
        );

      case 4:
        return (
          <StepCard
            badge="Step 2 of 2 — Verification"
            title="Take a selfie"
            subtitle="We'll compare this with your profile photo for verification."
            icon={Camera}
            subSteps={[
              { num: 1, label: 'Upload Photo', active: true, done: true },
              { num: 2, label: 'Take Selfie', active: true },
              { num: 3, label: 'Verification', active: false },
            ]}
          >
            <div className="rounded-2xl overflow-hidden bg-surface-900 border border-surface-700">
              {selfiePreview ? (
                <div className="relative">
                  <img src={selfiePreview} alt="Selfie" className="w-full h-64 object-cover" />
                  <button
                    type="button"
                    onClick={() => {
                      setSelfiePreview((prev) => {
                        revokeObjectUrlMaybe(prev);
                        return '';
                      });
                      setSelfieBlob(null);
                      startCamera();
                    }}
                    className="absolute bottom-3 right-3 rounded-xl bg-surface-900/80 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white hover:bg-surface-900 transition-colors"
                  >
                    Retake
                  </button>
                </div>
              ) : cameraActive ? (
                <div className="relative bg-black">
                  <video
                    ref={videoRef}
                    className="w-full h-64 object-cover"
                    autoPlay
                    playsInline
                    muted
                  />
                  <button
                    type="button"
                    onClick={takeSelfie}
                    aria-label="Capture selfie"
                    className="absolute bottom-4 left-1/2 -translate-x-1/2 h-14 w-14 rounded-full bg-white border-4 border-brand-500 shadow-lg hover:scale-105 transition-transform"
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={startCamera}
                  className="w-full h-64 flex flex-col items-center justify-center gap-3 hover:bg-surface-800/50 transition-colors"
                >
                  <div className="h-14 w-14 rounded-2xl bg-surface-800 flex items-center justify-center">
                    <Camera size={24} className="text-brand-400" />
                  </div>
                  <p className="text-sm font-semibold text-surface-300">Tap to open camera</p>
                </button>
              )}
            </div>
            <canvas ref={canvasRef} className="hidden" />
            {!selfiePreview && (
              <p className="mt-3 text-center text-xs text-surface-500 leading-relaxed px-1">
                <span className="text-surface-400">Mac:</span> use <strong className="text-surface-400">Open camera</strong> below (or the box above), click <strong className="text-surface-400">Allow</strong> if the browser asks, wait until you see your face, then <strong className="text-surface-400">Capture selfie</strong> or tap the white circle.
              </p>
            )}
          </StepCard>
        );

      case 5: {
        const verificationStepDone = verificationStatus === 'verified' || user?.faceVerified;
        const verifiedOnlyJourney = !!user?.faceVerified;
        return (
          <StepCard
            badge={verifiedOnlyJourney ? 'Face verified' : 'Step 2 of 2 — Verification'}
            title={verifiedOnlyJourney ? "You're all set" : 'Face Verification'}
            subtitle={
              verifiedOnlyJourney
                ? 'Your profile is already face verified. Continue to enable your location so we can show vibes near you.'
                : "We're comparing your profile photo with your selfie."
            }
            icon={verifiedOnlyJourney ? Sparkles : Shield}
            subSteps={
              verifiedOnlyJourney
                ? undefined
                : [
                    { num: 1, label: 'Upload Photo', active: true, done: true },
                    { num: 2, label: 'Take Selfie', active: true, done: true },
                    {
                      num: 3,
                      label: 'Verification',
                      active:
                        verificationStatus === 'verifying' ||
                        (!verificationStepDone && verificationStatus !== 'failed'),
                      done: verificationStepDone,
                    },
                  ]
            }
          >
            {verificationStatus === 'verifying' ? (
              <div className="flex flex-col items-center py-8">
                <Loader2 size={48} className="text-brand-400 animate-spin mb-4" />
                <p className="text-sm text-surface-300">Analyzing your photos...</p>
              </div>
            ) : verificationStatus === 'failed' ? (
              <div className="flex flex-col items-center py-6">
                <div className="h-16 w-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                  <X size={32} className="text-red-400" />
                </div>
                <p className="text-lg font-bold text-white mb-1">Verification Failed</p>
                <p className="text-sm text-surface-400 text-center mb-4">
                  {faceVerificationFailureMessage(verificationData) ||
                    'Photos could not be verified. Try clearer photos.'}
                </p>
                {verificationData?.failureStep === 'age' ? (
                  <p className="text-xs text-surface-500 text-center mb-3 max-w-sm">
                    Your details will be prefilled on the profile step; confirm or adjust name, age, and gender, then return here to verify again.
                  </p>
                ) : null}
                {verificationData?.failureStep === 'age' ? (
                  <button
                    type="button"
                    onClick={goToDetailsAfterAgeMismatch}
                    className="rounded-xl bg-brand-500/20 border border-brand-500/50 px-4 py-2 text-sm font-semibold text-brand-200 hover:bg-brand-500/30 transition-colors"
                  >
                    Review name, age & gender
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setProfilePreview((prev) => {
                        revokeObjectUrlMaybe(prev);
                        return '';
                      });
                      setSelfiePreview((prev) => {
                        revokeObjectUrlMaybe(prev);
                        return '';
                      });
                      setStep(3);
                      setVerificationStatus(null);
                      setProfileImage(null);
                      setSelfieBlob(null);
                    }}
                    className="rounded-xl bg-surface-800 px-4 py-2 text-sm font-semibold text-white hover:bg-surface-700 transition-colors"
                  >
                    Try Again
                  </button>
                )}
              </div>
            ) : verificationStepDone ? (
              <div className="flex flex-col items-center py-6">
                <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                  <Check size={32} className="text-green-400" />
                </div>
                <p className="text-lg font-bold text-white mb-1">Verified!</p>
                <p className="text-sm text-surface-400 text-center">
                  {verificationData?.similarity != null
                    ? `Similarity: ${verificationData.similarity}%`
                    : 'Your profile is face verified.'}
                </p>
                <div className="flex gap-2 mt-4">
                  <span className="rounded-full bg-green-500/10 px-3 py-1 text-xs font-semibold text-green-400">
                    Face Verified
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center py-6">
                <div className="flex gap-4 mb-6">
                  {(profilePreview
                    || user?.profileImage
                    || user?.profileImageUrl) && (
                    <div className="flex flex-col items-center gap-2">
                      <img
                        src={
                          profilePreview
                            || resolveProfileDisplaySrc(user, siteOrigin)
                            || user?.profileImageUrl
                            || ''
                        }
                        alt="Profile"
                        className="h-20 w-20 rounded-xl object-cover border-2 border-surface-700"
                      />
                      <span className="text-xs font-medium text-surface-500">Profile</span>
                    </div>
                  )}
                  {(selfiePreview || user?.selfieImageUrl) && (
                    <div className="flex flex-col items-center gap-2">
                      <img
                        src={selfiePreview || user.selfieImageUrl}
                        alt="Selfie"
                        className="h-20 w-20 rounded-xl object-cover border-2 border-surface-700"
                      />
                      <span className="text-xs font-medium text-surface-500">Selfie</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-surface-400 mb-4 text-center">
                  Ready to verify. We'll compare your photos using AI.
                </p>
              </div>
            )}
          </StepCard>
        );
      }

      case 6:
        return (
          <StepCard
            badge="Almost done!"
            title="Enable Location"
            subtitle="Allow location access so we can show vibes near you."
            icon={MapPin}
          >
            <div className="flex flex-col items-center py-6">
              <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-brand-500 to-violet-500 flex items-center justify-center mb-6 shadow-lg shadow-brand-500/20">
                <MapPin size={36} className="text-white" />
              </div>
              <p className="text-sm text-surface-400 text-center max-w-xs">
                We use your location to show nearby vibes and help you connect with people around you. This data is kept private.
              </p>
            </div>
          </StepCard>
        );

      default:
        return null;
    }
  };

  const getStepAction = () => {
    switch (step) {
      case 1: return handleIdentityStep;
      case 2: return handleBioAndInterests;
      case 3: return handleProfileUpload;
      case 4: return handleSelfieStepPrimary;
      case 5:
        if (verificationStatus === 'failed' && verificationData?.failureStep === 'age') {
          return goToDetailsAfterAgeMismatch;
        }
        return verificationStatus === 'verified' || verificationStatus === 'failed' || user?.faceVerified
          ? nextStep
          : handleVerification;
      case 6: return handleLocation;
      default: return nextStep;
    }
  };

  const getButtonLabel = () => {
    if (loading) return null;
    switch (step) {
      case 3: return profileImage ? 'Upload & Continue' : 'Select Photo';
      case 4:
        if (selfieBlob) return 'Upload & Continue';
        if (cameraActive) return 'Capture selfie';
        return 'Open camera';
      case 5:
        if (verificationStatus === 'failed' && verificationData?.failureStep === 'age') {
          return 'Review name, age & gender';
        }
        if (verificationStatus === 'verified' || verificationStatus === 'failed' || user?.faceVerified) return 'Continue';
        return 'Verify Now';
      case 6: return 'Allow Location & Finish';
      default: return 'Continue';
    }
  };

  return (
    <div className="min-h-screen bg-surface-950 flex flex-col">
      {/* Progress bar */}
      <div className="sticky top-0 z-20 bg-surface-950/80 backdrop-blur-xl">
        <div className="h-1.5 bg-surface-800 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-brand-500 to-violet-500 transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg">
          {renderStep()}

          {/* Actions */}
          <div className="mt-6 space-y-3">
            <button
              type="button"
              onClick={getStepAction()}
              disabled={loading}
              className="w-full rounded-xl bg-gradient-to-r from-brand-500 via-violet-500 to-purple-500 py-4 text-sm font-bold text-white shadow-lg shadow-brand-500/20 transition-all hover:shadow-xl hover:shadow-brand-500/30 hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  {step === 5 ? 'Verifying...' : step === 4 && selfieBlob ? 'Uploading...' : 'Saving...'}
                </span>
              ) : (
                <>
                  {getButtonLabel()}
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            <div className="flex items-center justify-between">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex items-center gap-1.5 text-sm text-surface-400 hover:text-white transition-colors"
                >
                  <ArrowLeft size={14} />
                  Back
                </button>
              ) : (
                <div />
              )}
              {step < TOTAL_STEPS &&
                !STEPS_SKIP_DISALLOWED.has(step) &&
                !(step === 5 && (user?.faceVerified || verificationStatus === 'verified')) && (
                <button
                  type="button"
                  onClick={skipAndContinue}
                  className="text-sm text-surface-500 hover:text-surface-300 transition-colors"
                >
                  Skip for now
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StepCard = ({ badge, title, subtitle, icon: IconComp, children, subSteps }) => (
  <div className="rounded-2xl border border-surface-800 bg-surface-900/60 backdrop-blur-xl p-6 sm:p-8 shadow-2xl shadow-black/20">
    {badge && (
      <div className="flex justify-center mb-4">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-800 border border-surface-700 px-3.5 py-1.5 text-xs font-semibold text-surface-300">
          <Sparkles size={12} className="text-brand-400" />
          {badge}
        </span>
      </div>
    )}

    {subSteps && (
      <div className="flex items-center justify-center gap-1 mb-5">
        {subSteps.map(({ num, label, active, done }, idx) => (
          <div key={num} className="flex items-center gap-1">
            <div className={`flex items-center gap-1.5 ${idx > 0 ? 'ml-1' : ''}`}>
              <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${
                done ? 'bg-brand-500 text-white' : active ? 'bg-brand-500/20 text-brand-400 border border-brand-500' : 'bg-surface-800 text-surface-500'
              }`}>
                {done ? <Check size={12} /> : num}
              </div>
              <span className={`text-xs font-medium ${active ? 'text-surface-300' : 'text-surface-600'}`}>
                {label}
              </span>
            </div>
            {idx < subSteps.length - 1 && (
              <div className={`w-6 h-px mx-1 ${done ? 'bg-brand-500' : 'bg-surface-700'}`} />
            )}
          </div>
        ))}
      </div>
    )}

    {IconComp && (
      <div className="flex justify-center mb-4">
        <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-brand-500 to-violet-500 flex items-center justify-center shadow-lg shadow-brand-500/20">
          <IconComp size={28} className="text-white" />
        </div>
      </div>
    )}

    <h2 className="text-2xl sm:text-3xl font-extrabold text-white text-center mb-2">
      {title}
    </h2>
    <p className="text-sm text-surface-400 text-center mb-6">
      {subtitle}
    </p>

    {children}
  </div>
);

export default OnboardingPage;
